/*
 * Postgres 컬럼이 snake_case 라 행 → 도메인 변환 지점에서만 규칙을 끈다
 * (변환 이후 도메인 타입은 전부 camelCase — matching.api 와 같은 이유·같은 방식).
 */
/* eslint-disable @typescript-eslint/naming-convention */
import { supabase } from '~/common/utils/supabase.util.ts';

import type { Tutor } from '~/features/tutor/types/tutor.type';

/**
 * 지도사 조회 어댑터.
 *
 * @description `profiles` 중 `role = 'tutor'` 인 행이 지도사다.
 *
 *  **로그인 없이 조회한다.** 홈·지도사 상세는 둘러보기 화면이라 anon 키로도 읽혀야 한다
 *  (마이그레이션 0009 가 anon 에게 지도사 행 + 표시용 컬럼만 열어 두었다 — 보호자 프로필은 안 보인다).
 *  그래서 `getAuthedClient()` 가 아니라 기본 클라이언트를 쓴다.
 */

interface TutorRow {
  id: string;
  slug: string | null;
  nickname: string;
  career_label: string | null;
  career_years: number | null;
  intro: string | null;
  active_area: string | null;
  rating: number | null;
  review_count: number | null;
  avatar_emoji: string | null;
}

/** 표시용 컬럼만 고른다 — `select('*')` 는 anon 에게 없는 컬럼까지 요청해 권한 오류가 난다. */
const TUTOR_COLUMNS =
  'id, slug, nickname, career_label, career_years, intro, active_area, rating, review_count, avatar_emoji';

/**
 * 행 → 도메인 변환.
 * @description 가입 직후 지도사는 소개·경력을 아직 안 채웠을 수 있다. 화면이 `undefined` 를 그리지
 *  않도록 빈 값으로 메운다(프로필 미작성은 오류가 아니라 정상 상태다).
 */
function toTutor(row: TutorRow): Tutor {
  return {
    id: row.id,
    slug: row.slug ?? row.id,
    name: row.nickname,
    careerLabel: row.career_label ?? '',
    careerYears: row.career_years ?? 0,
    intro: row.intro ?? '',
    activeArea: row.active_area ?? '',
    rating: row.rating === null ? null : Number(row.rating),
    reviewCount: row.review_count ?? 0,
    emoji: row.avatar_emoji ?? '🧑‍🏫',
  };
}

export async function fetchTutorsApi(): Promise<Tutor[]> {
  if (!supabase) return [];

  const { data, error } = await supabase
    .from('profiles')
    .select(TUTOR_COLUMNS)
    .eq('role', 'tutor')
    .order('created_at', { ascending: true });

  if (error) throw error;
  return ((data as unknown as TutorRow[] | null) ?? []).map(toTutor);
}
